'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createHumanReviewDecision,
  type HumanReviewDecisionResult,
  type HumanReviewRequest,
} from '@/lib/admin/client';
import {
  humanReviewRequestPrefixKey,
  humanReviewRequestsPrefixKey,
} from '@/lib/admin/queryKeys';

export type DecideVariables = {
  request: HumanReviewRequest;
  result: HumanReviewDecisionResult;
  reason: string;
};

// The mutation propagates typed decision conflicts so the panel can distinguish
// a stale sequence from a request that expired before it was submitted.
// onSettled refetches both list and detail against the server-authoritative state.
export function useHumanReviewDecision() {
  const { getToken, userId, sessionId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ request, result, reason }: DecideVariables) => {
      const token = await getToken();
      if (!token) throw new Error('Missing auth token');
      return createHumanReviewDecision(token, {
        requestId: request.id,
        result,
        reason,
        expectedSequence: request.latestSequence,
      });
    },
    onSettled: () => {
      const identity = { userId, sessionId };
      void queryClient.invalidateQueries({
        queryKey: humanReviewRequestsPrefixKey(identity),
      });
      void queryClient.invalidateQueries({
        queryKey: humanReviewRequestPrefixKey(identity),
      });
    },
  });
}
